import { useHead } from '../lib/head.js'
import { site, siteOrigin } from '../data/site.js'

/* ------------------------------------------------------------------ *
 *  JSON-LD structured data
 *
 *  Search engines read these to build the knowledge panel, the sitelinks
 *  search box and the rich result for a service or a job. They are what
 *  turns a plain blue link into a result with the company name, logo,
 *  location and rating attached.
 *
 *  <SiteStructuredData /> is emitted once, from the shared Layout, and
 *  carries the facts that describe the company itself. The per-page
 *  helpers below add whatever that page is actually about.
 * ------------------------------------------------------------------ */

/* Functions, not constants: the origin is a property of the request, and at
   module-import time during the prerender there is no window to read it from. */
const ORG_ID = () => `${siteOrigin()}/#organization`
const SITE_ID = () => `${siteOrigin()}/#website`

function Ld({ data }) {
  useHead([
    { tag: 'script', attrs: { type: 'application/ld+json' }, text: JSON.stringify(data) },
  ])
  return null
}

/** Company + site identity. Rendered once for every page. */
export function SiteStructuredData() {
  return (
    <Ld
      data={{
        '@context': 'https://schema.org',
        '@graph': [
          {
            '@type': 'Organization',
            '@id': ORG_ID(),
            name: site.name,
            legalName: 'Spectranox Technologies Pvt Ltd',
            /* Registered as Aweteck Services Pvt. Ltd in 2020 and
               renamed in 2026. Search engines use alternateName to connect
               searches for the old name to this company. */
            alternateName: ['Spectranox Technologies', 'Aweteck Services Pvt. Ltd'],
            url: siteOrigin(),
            logo: `${siteOrigin()}/images/spectranox.webp`,
            description: site.description,
            foundingDate: '2020-05-02',
            email: site.email,
            telephone: site.phone,
            address: {
              '@type': 'PostalAddress',
              addressLocality: site.city,
              addressRegion: site.region,
              addressCountry: 'IN',
            },
            areaServed: site.areaServed.map((n) => ({ '@type': 'Country', name: n })),
            sameAs: Object.values(site.social).filter((u) => u && !u.endsWith('.com/')),
          },
          {
            '@type': 'WebSite',
            '@id': SITE_ID(),
            url: siteOrigin(),
            name: site.name,
            publisher: { '@id': ORG_ID() },
            inLanguage: 'en',
          },
          {
            '@type': 'ProfessionalService',
            name: site.name,
            image: `${siteOrigin()}/images/spectranox.webp`,
            url: siteOrigin(),
            telephone: site.phone,
            email: site.email,
            priceRange: '$$',
            address: {
              '@type': 'PostalAddress',
              addressLocality: site.city,
              addressRegion: site.region,
              addressCountry: 'IN',
            },
            areaServed: site.areaServed.map((n) => ({ '@type': 'Country', name: n })),
            makesOffer: site.offerings.map((o) => ({
              '@type': 'Offer',
              itemOffered: { '@type': 'Service', name: o },
            })),
          },
        ],
      }}
    />
  )
}

/** One service page. `name` and `description` come from the page itself. */
export function ServiceStructuredData({ name, description, path }) {
  return (
    <Ld
      data={{
        '@context': 'https://schema.org',
        '@type': 'Service',
        name,
        description,
        url: `${siteOrigin()}${path}`,
        serviceType: name,
        provider: { '@id': ORG_ID() },
        areaServed: site.areaServed.map((n) => ({ '@type': 'Country', name: n })),
      }}
    />
  )
}

/** A case study. Google shows these as article-style results. */
export function ProjectStructuredData({ name, description, image, path, year }) {
  return (
    <Ld
      data={{
        '@context': 'https://schema.org',
        '@type': 'CreativeWork',
        name,
        description,
        url: `${siteOrigin()}${path}`,
        ...(image ? { image } : {}),
        ...(year ? { dateCreated: String(year) } : {}),
        creator: { '@id': ORG_ID() },
      }}
    />
  )
}

/** A job opening. This is what puts a listing into Google Jobs. */
export function JobStructuredData({ title, description, path, location, employmentType, datePosted }) {
  return (
    <Ld
      data={{
        '@context': 'https://schema.org',
        '@type': 'JobPosting',
        title,
        description,
        url: `${siteOrigin()}${path}`,
        ...(datePosted ? { datePosted } : {}),
        ...(employmentType ? { employmentType } : {}),
        hiringOrganization: { '@id': ORG_ID() },
        jobLocation: {
          '@type': 'Place',
          address: {
            '@type': 'PostalAddress',
            addressLocality: location || site.city,
            addressRegion: site.region,
            addressCountry: 'IN',
          },
        },
      }}
    />
  )
}

/** The trail a result shows under its title. */
export function BreadcrumbStructuredData({ trail }) {
  return (
    <Ld
      data={{
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: trail.map((t, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          name: t.name,
          item: `${siteOrigin()}${t.path}`,
        })),
      }}
    />
  )
}

/** Turns an FAQ section into an expandable rich result. */
export function FaqStructuredData({ items }) {
  return (
    <Ld
      data={{
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: items.map((q) => ({
          '@type': 'Question',
          name: q.q,
          acceptedAnswer: { '@type': 'Answer', text: q.a },
        })),
      }}
    />
  )
}

export default SiteStructuredData
